import React, { useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'

import { DefaultOrgIcon } from '~assets/icons'
import { type RootState } from '~redux/rootReducer'
import { selectWorkspaceOrgs } from '~redux/slices/organizationSlice'
import { type EmailIdentity } from '~types'

import Avatar from '../Avatar'
import Dropdown, { type DropdownOption } from '../Dropdown'
import OrganizationChip from '../OrganizationChip'

export interface ContactBookSelectorProps {
  value?: string
  onSelect?: (book: EmailIdentity) => void
  searchable?: boolean
  searchThreshold?: number
  placeholder?: string
  disabled?: boolean
  fullWidth?: boolean
  label?: string
}

const ContactBookSelector: React.FC<ContactBookSelectorProps> = ({
  value,
  onSelect,
  searchable,
  searchThreshold = 10,
  placeholder = 'Select contact book',
  disabled = false,
  fullWidth = false,
  label = 'Contact Book',
}) => {
  const contactBooks = useSelector(selectWorkspaceOrgs)
  const reduxSelectedOrgId = useSelector(
    (state: RootState) => state?.organization.selectedOrganization?.id,
  )
  const orgRefreshTrigger = useSelector((state: RootState) => state?.organization.refreshTrigger)

  const initialBookId = value ?? reduxSelectedOrgId ?? contactBooks[0]?.id

  const [selectedId, setSelectedId] = useState<string | undefined>(initialBookId)

  useEffect(() => {
    if (initialBookId && initialBookId !== selectedId) {
      setSelectedId(initialBookId)
    }
  }, [initialBookId])

  useEffect(() => {
    if (!selectedId) return
    const book = contactBooks.find((b) => b.id === selectedId)
    if (book) onSelect?.(book)
  }, [orgRefreshTrigger])

  useEffect(() => {
    if (!selectedId && contactBooks.length > 0) {
      const first = contactBooks[0]
      setSelectedId(first.id)
      onSelect?.(first)
    }
  }, [contactBooks.length])

  const options: DropdownOption[] = useMemo(
    () =>
      contactBooks.map((book) => {
        const logoSrc = book.image ?? book.workspace_icon
        return {
          value: book.id,
          label: book.name ?? '',
          avatar: logoSrc ? (
            <Avatar size='sm' src={logoSrc} alt={book.name ?? 'Contact book'} />
          ) : (
            <DefaultOrgIcon width={20} height={20} />
          ),
        }
      }),
    [contactBooks],
  )

  if (contactBooks.length === 0) return null

  if (contactBooks.length === 1) {
    const only = contactBooks[0]
    return (
      <OrganizationChip
        orgName={only.name ?? ''}
        orgIcon={only.image ?? only.workspace_icon ?? undefined}
      />
    )
  }

  const handleChange = (next: string | string[]): void => {
    const id = Array.isArray(next) ? next[0] : next
    setSelectedId(id)
    const book = contactBooks.find((b) => b.id === id)
    if (book) onSelect?.(book)
  }

  return (
    <Dropdown
      label={label}
      options={options}
      value={selectedId}
      onChange={handleChange}
      placeholder={placeholder}
      disabled={disabled}
      searchable={searchable}
      searchThreshold={searchThreshold}
      fullWidth={fullWidth}
      containerSx={fullWidth ? undefined : { minWidth: 300 }}
    />
  )
}

export default ContactBookSelector
